import { useI18n } from '../../../shared/i18n';
import { localePath } from '../../../shared/i18n/routing';
import type { PublicAsset } from '../../../shared/lib';
import type { PublicTechnology } from '../publicApi';

export interface TechnologyCardProps {
  technology: PublicTechnology;
  icon: PublicAsset | undefined;
}

/**
 * Up to two characters, taken from the name itself rather than word
 * boundaries — `initialsOf` (Avatar.tsx) is for person names, and most
 * technology names are one word ("React", "PostgreSQL"), which would
 * collapse to a single letter under that rule.
 *
 * Uses code points rather than `charAt` for the same reason `initialsOf`
 * does: a name outside the Basic Multilingual Plane would otherwise split a
 * surrogate pair.
 */
export function techMonogram(name: string): string {
  return [...name].slice(0, 2).join('').toLocaleUpperCase();
}

export function TechnologyCard({ technology, icon }: TechnologyCardProps) {
  const { locale, t } = useI18n();

  return (
    <a
      href={localePath(locale, `/technologies/${technology.slug}`)}
      className="hs-card group flex flex-col gap-2 rounded-lg p-4 transition-all duration-[--hs-duration-base] ease-out hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-md"
    >
      <div className="flex items-center gap-2.5">
        {icon ? (
          <img src={icon.url} alt="" className="size-8 shrink-0" aria-hidden="true" />
        ) : (
          <span
            aria-hidden="true"
            className="hs-meta flex size-8 shrink-0 items-center justify-center rounded-md bg-accent-subtle text-accent"
          >
            {techMonogram(technology.name)}
          </span>
        )}
        <span className="font-medium text-text">{technology.name}</span>
      </div>
      {technology.description ? (
        <p className="line-clamp-2 text-sm text-text-muted">{technology.description}</p>
      ) : null}
      <p className="hs-meta mt-auto text-text-subtle">
        {t('common.technologies.projectCount', { count: technology.project_count })}
      </p>
    </a>
  );
}
